/**
 * AudioRecorder - Handles audio recording using AudioWorklet
 */
import EventEmitter from './EventEmitter.js';

export default class AudioRecorder extends EventEmitter {
  constructor(config) {
    super();
    this.config = config;
    this.audioContext = null;
    this.audioWorkletNode = null;
    this.mediaStream = null;
    this.isRecording = false;
  }
  
  /**
   * Get the audio processor path
   * Tries to detect from script source, falls back to config or default
   */
  getAudioProcessorPath() {
    // If explicitly configured, use it
    if (this.config.audioProcessorPath) {
      return this.config.audioProcessorPath;
    }
    
    // Try to find the script that loaded the SDK
    const scripts = document.getElementsByTagName('script');
    for (let script of scripts) {
      const src = script.src;
      // Check if this is the agent-widget.js script
      if (src && (src.includes('agent-widget.js') || src.includes('ttp-agent-sdk'))) {
        // Extract base URL and construct processor path
        try {
          const url = new URL(src);
          const basePath = src.substring(0, src.lastIndexOf('/'));
          return `${basePath}/audio-processor.js`;
        } catch (e) {
          // If URL parsing fails, try to extract path manually
          const basePath = src.substring(0, src.lastIndexOf('/'));
          return `${basePath}/audio-processor.js`;
        }
      }
    }
    
    // Fallback to CDN
    return 'https://cdn.talktopc.com/audio-processor.js';
  }
  
  /**
   * Check microphone permission state
   * Returns 'granted', 'denied', 'prompt', or null if API not available
   */
  async checkPermissionState() {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'microphone' });
        return result.state;
      }
    } catch (error) {
      // Permissions API might not be supported or might throw for microphone
      // This is OK - we'll still try to request permission
      console.log('⚠️ AudioRecorder: Permissions API not available or not supported for microphone');
    }
    return null; // Unknown state
  }

  /**
   * Request microphone permission without starting recording
   * This is useful for mobile devices where we want to get permission before connecting
   * Returns true if permission granted, false otherwise
   */
  async requestPermission() {
    try {
      const permissionState = await this.checkPermissionState();
      
      // If already granted, return immediately
      if (permissionState === 'granted') {
        console.log('✅ AudioRecorder: Microphone permission already granted');
        return true;
      }
      
      // Request permission by calling getUserMedia
      // We'll immediately stop the stream after getting permission
      console.log('🎤 AudioRecorder: Requesting microphone permission...');
      const sampleRate = this.config.sampleRate || this.config.outputSampleRate || 44100;
      
      // Merge user-provided audioConstraints with defaults
      const defaultConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      };
      const audioConstraints = this.config.audioConstraints 
        ? { ...defaultConstraints, ...this.config.audioConstraints }
        : defaultConstraints;
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: sampleRate,
          channelCount: 1,
          ...audioConstraints
        }
      });
      
      // Stop the stream immediately - we just wanted permission
      mediaStream.getTracks().forEach(track => track.stop());
      
      console.log('✅ AudioRecorder: Microphone permission granted');
      return true;
    } catch (error) {
      console.error('❌ AudioRecorder: Failed to get microphone permission:', error);
      return false;
    }
  }

  /**
   * Start audio recording
   * Always requests fresh microphone permission - browsers will show the permission prompt
   * if permission hasn't been explicitly blocked by the user.
   */
  async start() {
    try {
      // Always try to request permission - we call getUserMedia every time
      // This ensures the prompt appears if the permission state allows it
      // Even if previously denied, the user might have changed settings, so we always try
      console.log('🎤 AudioRecorder: Requesting microphone access...');
      
      // Check permission state for logging/debugging (but don't block the request)
      const permissionState = await this.checkPermissionState();
      if (permissionState) {
        console.log(`📋 AudioRecorder: Current permission state: ${permissionState}`);
        if (permissionState === 'denied') {
          console.warn('⚠️ AudioRecorder: Permission state is "denied" - browser may not show prompt. User may need to change in browser settings.');
        }
      }
      
      // Use sampleRate if provided, fall back to outputSampleRate, then default to 44100 Hz
      // This ensures AudioContext matches server output sample rate (44100 Hz)
      const sampleRate = this.config.sampleRate || this.config.outputSampleRate || 44100;
      
      // Merge user-provided audioConstraints with defaults
      const defaultConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      };
      const audioConstraints = this.config.audioConstraints 
        ? { ...defaultConstraints, ...this.config.audioConstraints }
        : defaultConstraints;
      
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: sampleRate,
          channelCount: 1,
          ...audioConstraints
        }
      });
      
      // CRITICAL: Check connection status AFTER getting permission but BEFORE creating AudioWorkletNode
      // Server might have rejected during the async permission request
      if (this.config.checkConnection && typeof this.config.checkConnection === 'function') {
        const isConnected = this.config.checkConnection();
        if (!isConnected) {
          // Silent cleanup - server rejected, this is expected
          // Stop media stream immediately
          this.mediaStream.getTracks().forEach(track => track.stop());
          this.mediaStream = null;
          const error = new Error('Connection lost - server may have rejected the call');
          error.name = 'ServerRejected';
          error.isServerRejection = true;
          throw error;
        }
      }
      
      // Create AudioContext with the same sample rate
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: sampleRate
      });
      
      // Resume AudioContext if suspended
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      
      // CRITICAL: Check connection again BEFORE creating AudioWorkletNode (which starts VAD)
      if (this.config.checkConnection && typeof this.config.checkConnection === 'function') {
        const isConnected = this.config.checkConnection();
        if (!isConnected) {
          // Silent cleanup - server rejected, this is expected
          // Clean up media stream and AudioContext
          this.mediaStream.getTracks().forEach(track => track.stop());
          this.mediaStream = null;
          if (this.audioContext && this.audioContext.state !== 'closed') {
            await this.audioContext.close();
          }
          this.audioContext = null;
          const error = new Error('Connection lost - server may have rejected the call');
          error.name = 'ServerRejected';
          error.isServerRejection = true;
          throw error;
        }
      }
      
      // Get the audio processor path
      const processorPath = this.getAudioProcessorPath();
      
      // Load AudioWorklet module
      await this.audioContext.audioWorklet.addModule(processorPath);
      
      // CRITICAL: Check connection one more time BEFORE creating AudioWorkletNode
      // This is the last chance to prevent VAD from starting
      if (this.config.checkConnection && typeof this.config.checkConnection === 'function') {
        const isConnected = this.config.checkConnection();
        if (!isConnected) {
          // Silent cleanup - server rejected, this is expected
          // Clean up everything
          this.mediaStream.getTracks().forEach(track => track.stop());
          this.mediaStream = null;
          if (this.audioContext && this.audioContext.state !== 'closed') {
            await this.audioContext.close();
          }
          this.audioContext = null;
          const error = new Error('Connection lost - server may have rejected the call');
          error.name = 'ServerRejected';
          error.isServerRejection = true;
          throw error;
        }
      }
      
      // Create AudioWorklet node with processor options (sampleRate will be used by processor)
      this.audioWorkletNode = new AudioWorkletNode(this.audioContext, 'audio-processor', {
        processorOptions: {
          sampleRate: sampleRate,
          outputSampleRate: this.config.outputSampleRate
        }
      });
      
      // Create media stream source
      const source = this.audioContext.createMediaStreamSource(this.mediaStream);
      source.connect(this.audioWorkletNode);
      
      // Store source reference so we can disconnect it when stopping
      this.mediaStreamSource = source;
      
      // Handle messages from AudioWorklet
      this.audioWorkletNode.port.onmessage = (event) => {
        const { type, data } = event.data;
        
        if (type === 'pcm_audio_data') {
          this.emit('audioData', data);
        }
      };
      
      // Enable continuous mode
      this.audioWorkletNode.port.postMessage({
        type: 'setForceContinuous',
        data: { enabled: true }
      });
      
      console.log('✅ AudioRecorder: Microphone access granted');
      this.isRecording = true;
      this.emit('recordingStarted');
      
    } catch (error) {
      // Handle server rejection gracefully (don't emit error event)
      if (error.isServerRejection || error.name === 'ServerRejected') {
        // Server rejection is expected - don't emit error event, just throw
        throw error;
      }
      
      // Provide helpful error messages for permission issues
      let userMessage = error.message;
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        // Check if permission was explicitly denied (won't show prompt again)
        const permissionState = await this.checkPermissionState();
        
        if (permissionState === 'denied') {
          // Permission was explicitly blocked - browser won't show prompt again
          userMessage = 'Microphone access was blocked. To enable it: 1) Click the lock icon (🔒) in your browser\'s address bar, 2) Find "Microphone" and change it to "Allow", 3) Refresh this page and try again.';
          console.error('❌ AudioRecorder: Permission explicitly denied. User must change in browser settings.');
        } else if (permissionState === 'prompt') {
          // Permission is in prompt state - user can try again and should see prompt
          userMessage = 'Microphone permission was denied. Click the "Voice Call" button again to see the permission prompt.';
          console.error('❌ AudioRecorder: Permission denied but state is "prompt" - user can try again.');
        } else {
          // Unknown state - provide general guidance
          userMessage = 'Microphone permission was denied. Click the button again to retry, or enable microphone access in your browser settings (lock icon 🔒 in address bar).';
          console.error('❌ AudioRecorder: Permission denied. User can try again or change in browser settings.');
        }
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        userMessage = 'No microphone found. Please connect a microphone and try again.';
        console.error('❌ AudioRecorder: No microphone device found.');
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        userMessage = 'Microphone is already in use by another application. Please close other apps using the microphone and try again.';
        console.error('❌ AudioRecorder: Microphone is busy or not readable.');
      } else if (error.name === 'SecurityError' || error.name === 'PermissionDeniedError') {
        userMessage = 'Microphone access blocked. On mobile devices, please tap the recording button directly (not from an iframe or popup).';
        console.error('❌ AudioRecorder: User agent blocked microphone access. This often happens on mobile when not called from direct user interaction.');
      }
      
      const enhancedError = new Error(userMessage);
      enhancedError.name = error.name;
      enhancedError.originalError = error;
      
      this.emit('error', enhancedError);
      throw enhancedError;
    }
  }
  
  /**
   * Stop audio recording
   */
  async stop() {
    if (!this.isRecording) {
      return;
    }
    
    try {
      // CRITICAL: Disconnect source FIRST to stop audio flow immediately
      if (this.mediaStreamSource) {
        try {
          console.log('🛑 AudioRecorder: Disconnecting media stream source to stop audio flow...');
          this.mediaStreamSource.disconnect();
          this.mediaStreamSource = null;
          console.log('✅ AudioRecorder: Media stream source disconnected');
        } catch (e) {
          console.warn('⚠️ AudioRecorder: Error disconnecting source:', e);
        }
      }
      
      // CRITICAL: Send 'stop' message to AudioWorklet to stop VAD processing
      if (this.audioWorkletNode) {
        console.log('🛑 AudioRecorder: Sending stop message to AudioWorklet to stop VAD...');
        this.audioWorkletNode.port.postMessage({ type: 'stop' });
        // Wait longer to ensure processor receives stop message and terminates
        await new Promise(resolve => setTimeout(resolve, 150));
        
        // Flush any remaining audio data
        this.audioWorkletNode.port.postMessage({ type: 'flush' });
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Disconnect AudioWorkletNode to stop processing (this stops audio flow)
        try {
          this.audioWorkletNode.disconnect();
          console.log('✅ AudioRecorder: AudioWorkletNode disconnected');
        } catch (e) {
          // Ignore if already disconnected
          console.log('⚠️ AudioRecorder: AudioWorkletNode already disconnected');
        }
      }
      
      // Stop media stream tracks (this stops microphone access)
      if (this.mediaStream) {
        console.log('🛑 AudioRecorder: Stopping media stream tracks...');
        this.mediaStream.getTracks().forEach(track => {
          track.stop();
          console.log('✅ AudioRecorder: Stopped track:', track.kind);
        });
        this.mediaStream = null;
      }
      
      // Close AudioContext (this fully stops all audio processing)
      if (this.audioContext && this.audioContext.state !== 'closed') {
        console.log('🛑 AudioRecorder: Closing AudioContext...');
        await this.audioContext.close();
        this.audioContext = null;
        console.log('✅ AudioRecorder: AudioContext closed');
      }
      
      this.audioWorkletNode = null;
      this.isRecording = false;
      this.emit('recordingStopped');
      console.log('✅ AudioRecorder: Recording stopped, VAD should be stopped');
      
    } catch (error) {
      this.emit('error', error);
      throw error;
    }
  }
  
  /**
   * Resume AudioContext if suspended (e.g., after tab becomes visible again)
   * This is important because browsers suspend AudioContext when tab is hidden
   */
  async resumeAudioContext() {
    if (!this.isRecording || !this.audioContext) {
      return;
    }

    try {
      // Resume AudioContext if suspended
      if (this.audioContext.state === 'suspended') {
        console.log('🔄 AudioRecorder: Resuming suspended AudioContext...');
        await this.audioContext.resume();
        console.log('✅ AudioRecorder: AudioContext resumed');
      }

      // Check if media stream tracks are still active
      if (this.mediaStream) {
        const audioTracks = this.mediaStream.getAudioTracks();
        const activeTracks = audioTracks.filter(track => track.readyState === 'live');
        
        if (activeTracks.length === 0 && audioTracks.length > 0) {
          console.warn('⚠️ AudioRecorder: All audio tracks are inactive. Recording may have stopped.');
          // Tracks are inactive - this might require restarting recording
          // But we'll let the user know via an event
          this.emit('tracksInactive');
        }
      }
    } catch (error) {
      console.error('❌ AudioRecorder: Failed to resume AudioContext:', error);
      this.emit('error', error);
    }
  }

  /**
   * Get recording status
   */
  getStatus() {
    return {
      isRecording: this.isRecording,
      audioContextState: this.audioContext ? this.audioContext.state : 'closed'
    };
  }

  /**
   * Cleanup resources
   */
  destroy() {
    this.stop();
  }
}
